import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag, MapPin, Minus, Plus, X, AlertCircle,
} from 'lucide-react';
import { useToast, Button, Input } from '../components/ui';
import { fmtMoney } from '../utils/currency';

const PublicOrder = () => {
  const { toast } = useToast();
  const { code } = useParams();
  const [table, setTable] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTableAndMenu();
  }, [code]);

  const fetchTableAndMenu = async () => {
    try {
      const tableRes = await axios.get(`/api/qrcode/verify/${code}`);
      setTable(tableRes.data.table);

      const menuRes = await axios.get('/api/menu');
      setMenuItems(menuRes.data.filter(item => item.isAvailable));
      setLoading(false);
    } catch (err) {
      setError('Invalid QR code or table not found');
      setLoading(false);
    }
  };

  const addToOrder = (item) => {
    const existing = orderItems.find(i => i.menuItem === item._id);
    if (existing) {
      setOrderItems(orderItems.map(i =>
        i.menuItem === item._id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setOrderItems([...orderItems, {
        menuItem: item._id,
        name: item.name,
        price: item.price,
        quantity: 1,
      }]);
    }
  };

  const changeQty = (id, delta) => {
    setOrderItems(prev =>
      prev
        .map(i => (i.menuItem === id ? { ...i, quantity: i.quantity + delta } : i))
        .filter(i => i.quantity > 0)
    );
  };

  const removeFromOrder = (id) => {
    setOrderItems(orderItems.filter(i => i.menuItem !== id));
  };

  const total = orderItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const itemCount = orderItems.reduce((sum, i) => sum + i.quantity, 0);

  // Group menu by category for nicer browsing
  const groupedMenu = menuItems.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const placeOrder = async () => {
    if (orderItems.length === 0) {
      toast.warning('Please add items to your order');
      return;
    }
    if (!customerInfo.name || !customerInfo.phone) {
      toast.warning('Please enter your name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/orders/public', {
        items: orderItems.map(item => ({ menuItem: item.menuItem, quantity: item.quantity })),
        tableNumber: table?.tableNumber,
        customerInfo,
        qrCode: code,
      });
      toast.success('Order placed! Your order is being prepared.');
      setOrderItems([]);
      setCustomerInfo({ name: '', phone: '' });
    } catch (err) {
      toast.error('Error placing order: ' + (err.response?.data?.msg || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center animate-pulse">
            <ShoppingBag size={20} className="text-white" />
          </div>
          <p className="text-sm text-neutral-600">Loading menu…</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-8 max-w-md text-center">
          <div className="w-12 h-12 mx-auto bg-danger-100 rounded-full flex items-center justify-center mb-3">
            <AlertCircle size={24} className="text-danger-600" />
          </div>
          <h2 className="text-lg font-bold text-neutral-900">QR code invalid</h2>
          <p className="text-sm text-neutral-600 mt-1">{error}</p>
          <p className="text-xs text-neutral-500 mt-3">
            Please ask a staff member for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero header */}
      <header className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-sm">
              <img src="/chenab-logo.jpeg" alt="Chenab System" className="w-full h-full object-contain" />
            </div>
            <span className="text-base font-semibold tracking-tight">Chenab System</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Welcome 👋</h1>
          <p className="text-primary-100 text-sm mt-1 flex items-center gap-1.5">
            <MapPin size={14} />
            You're ordering from <span className="font-semibold">Table {table?.tableNumber}</span>
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-32 lg:pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu */}
          <div className="lg:col-span-2 space-y-5">
            {Object.entries(groupedMenu).length === 0 ? (
              <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-8 text-center">
                <p className="text-sm text-neutral-500">No menu items available right now.</p>
              </div>
            ) : (
              Object.entries(groupedMenu).map(([category, items]) => (
                <section key={category} className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
                  <div className="px-5 py-3 border-b border-neutral-200">
                    <h2 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                      {category}
                    </h2>
                  </div>
                  <ul className="divide-y divide-neutral-100">
                    {items.map(item => {
                      const inCart = orderItems.find(i => i.menuItem === item._id)?.quantity || 0;
                      return (
                        <li key={item._id} className="p-4 flex items-start justify-between gap-4 hover:bg-neutral-50/50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-neutral-900">{item.name}</h3>
                            {item.description && (
                              <p className="text-sm text-neutral-500 mt-0.5 line-clamp-2">{item.description}</p>
                            )}
                            <p className="text-base font-bold text-primary-600 mt-1.5 tabular-nums">
                              {fmtMoney(Number(item.price), true)}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            {inCart > 0 ? (
                              <div className="flex items-center gap-1.5 bg-neutral-100 rounded-md p-1">
                                <button
                                  onClick={() => changeQty(item._id, -1)}
                                  className="w-7 h-7 rounded bg-white border border-neutral-200 hover:bg-neutral-100 flex items-center justify-center text-neutral-700 transition-colors"
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="text-sm font-semibold w-6 text-center tabular-nums">{inCart}</span>
                                <button
                                  onClick={() => addToOrder(item)}
                                  className="w-7 h-7 rounded bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center transition-colors"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                            ) : (
                              <Button size="sm" variant="primary" leftIcon={<Plus size={14} />} onClick={() => addToOrder(item)}>
                                Add
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>

          {/* Order panel (sticky on desktop, fixed-bottom drawer on mobile) */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="hidden lg:block bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
              <OrderPanelContent
                orderItems={orderItems}
                itemCount={itemCount}
                total={total}
                customerInfo={customerInfo}
                setCustomerInfo={setCustomerInfo}
                changeQty={changeQty}
                removeFromOrder={removeFromOrder}
                placeOrder={placeOrder}
                submitting={submitting}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Mobile bottom bar */}
      {orderItems.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-card-hover z-40">
          <details className="group">
            <summary className="px-4 py-3 flex items-center justify-between cursor-pointer list-none">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {itemCount}
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{itemCount} item{itemCount === 1 ? '' : 's'} in cart</p>
                  <p className="text-lg font-bold text-neutral-900 tabular-nums">{fmtMoney(total, true)}</p>
                </div>
              </div>
              <span className="text-xs text-primary-600 font-medium group-open:hidden">View cart ↑</span>
              <span className="text-xs text-neutral-500 hidden group-open:inline">Collapse ↓</span>
            </summary>
            <div className="max-h-[70vh] overflow-y-auto border-t border-neutral-200">
              <OrderPanelContent
                orderItems={orderItems}
                itemCount={itemCount}
                total={total}
                customerInfo={customerInfo}
                setCustomerInfo={setCustomerInfo}
                changeQty={changeQty}
                removeFromOrder={removeFromOrder}
                placeOrder={placeOrder}
                submitting={submitting}
              />
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

const OrderPanelContent = ({
  orderItems, itemCount, total,
  customerInfo, setCustomerInfo,
  changeQty, removeFromOrder, placeOrder, submitting,
}) => (
  <>
    <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">Your order</h2>
      <span className="text-xs text-neutral-500">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
    </div>

    <div className="p-5 space-y-4">
      <div className="space-y-3">
        <Input
          label="Your name"
          type="text"
          placeholder="Full name"
          value={customerInfo.name}
          onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
          required
        />
        <Input
          label="Phone number"
          type="tel"
          placeholder="+92-..."
          value={customerInfo.phone}
          onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
          required
        />
      </div>

      {orderItems.length === 0 ? (
        <div className="py-6 text-center border-t border-neutral-100">
          <p className="text-sm text-neutral-500">Cart is empty</p>
          <p className="text-xs text-neutral-400 mt-1">Tap menu items to add them</p>
        </div>
      ) : (
        <ul className="space-y-3 border-t border-neutral-100 pt-3">
          {orderItems.map(item => (
            <li key={item.menuItem} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">{item.name}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <button
                    onClick={() => changeQty(item.menuItem, -1)}
                    className="w-7 h-7 rounded border border-neutral-200 bg-white hover:bg-neutral-100 text-neutral-700 flex items-center justify-center transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="text-sm font-medium w-7 text-center tabular-nums">{item.quantity}</span>
                  <button
                    onClick={() => changeQty(item.menuItem, 1)}
                    className="w-7 h-7 rounded border border-neutral-200 bg-white hover:bg-neutral-100 text-neutral-700 flex items-center justify-center transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    onClick={() => removeFromOrder(item.menuItem)}
                    className="ml-1 text-neutral-400 hover:text-danger-600 transition-colors"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <p className="text-sm font-semibold text-neutral-900 tabular-nums whitespace-nowrap">
                {fmtMoney(item.price * item.quantity, true)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>

    <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50">
      <div className="flex justify-between items-baseline mb-3">
        <span className="text-sm font-medium text-neutral-600">Total</span>
        <span className="text-2xl font-bold text-neutral-900 tabular-nums">{fmtMoney(total, true)}</span>
      </div>
      <Button
        variant="success"
        size="lg"
        fullWidth
        disabled={orderItems.length === 0}
        loading={submitting}
        onClick={placeOrder}
      >
        Place order
      </Button>
    </div>
  </>
);

export default PublicOrder;
